/**
 * @file 自定义弹框
 * @author svon.me@gmail.com
 */

import * as _ from "lodash-es";
import * as formUI from "../form";
import {i18n} from "@/locales/index";
import safeGet from "@fengqiaogang/safe-get";
import {ElIcon, ElInput, ElMessageBox} from "element-plus";
import Loading from "@/components/loading/index.vue";
import {h as createElement, ref, defineComponent} from "vue";
import {InfoFilled, SuccessFilled, WarningFilled, CircleCloseFilled} from "@element-plus/icons-vue";

const ACTION_TYPE = {
  CONFIRM: "confirm",
  CANCEL: "cancel"
}

/**
 * 对话框
 * @param {*} content  内容
 * @param {*} title    标题
 * @param {*} option   配置
 * @param {*} props    如果 content 为 VNode, 则会传递给 content
 * @returns
 */
export const confirm = async function (content, title, option = {}, props = {}) {
  if (content && _.isFunction(content)) {
    const value = await content();
    return confirm(value, title, option, props);
  }
  let view;
  let result;
  let isLoading = ref(false);
  let modalConfig = {};

  // 取消按钮 & 确定按钮回调方法
  const onCallback = async function (sandbox, action) {
    const app = action === ACTION_TYPE.CONFIRM ? safeGet(sandbox, "submit") : safeGet(sandbox, "cancel");
    if (app) {
      const res = await app();
      if (res && typeof res === "function") {
        return onCallback({submit: res}, action);
      }
      return res;
    } else if (action === ACTION_TYPE.CONFIRM) {
      return true;
    }
    return void 0;
  }

  const onActionHook = async function (action, done) {
    isLoading.value = true;
    let status;
    if (action === ACTION_TYPE.CONFIRM || action === ACTION_TYPE.CANCEL) {
      result = await onCallback(view ? view.value : void 0, action);
      status = result;
    }
    if (result) {
      const onOk = safeGet(option, 'onOk');
      const onCancel = safeGet(option, 'onCancel');
      if (onOk && action === ACTION_TYPE.CONFIRM) {
        status = await onOk(result);
      }
      if (onCancel && action === ACTION_TYPE.CANCEL) {
        status = await onCancel(result);
      }
    }
    isLoading.value = false;
    if (typeof status === "boolean" && status === false) {
      return false;
    }
    return done();
  }

  return new Promise(async function (resolve) {
    const Message = defineComponent({
      setup () {
        view = ref();
        return () => {
          let data;
          const className = _.concat([], safeGet(option, "class") || ["px-5", "pb-2", "py-5"]);

          if (safeGet(option, "type") === "drawer") {
            className.push("h-screen");
          } else {
            className.push("!static", safeGet(option, "height") || "min-h-50");
          }
          if (_.isString(content) || _.isNumber(content) || _.isArray(content)) {
            data = createElement("div", {}, content);
          } else {
            data = createElement(content, {
              ...props,
              ref: view,
              onSubmit: async function (e) {
                if (modalConfig && modalConfig.onVanish) {
                  onActionHook(ACTION_TYPE.CANCEL, function () {
                    modalConfig.onVanish();
                    resolve(result || (e || true));
                  });
                }
              },
              onCancel: async function (e) {
                // 拦截 input file 暴露的 cancel 事件
                const target = e?.target;
                if (target) {
                  const tagName = (target.tagName || "").toLowerCase();
                  const type = target.getAttribute ? target.getAttribute("type") : "";
                  if (tagName === "input" && type === "file") {
                    return false;
                  }
                }
                // 正常关闭事件
                if (modalConfig && modalConfig.onVanish) {
                  onActionHook(ACTION_TYPE.CANCEL, function () {
                    modalConfig.onVanish();
                    resolve(result || e);
                  });
                }
              }
            });
          }
          return createElement(Loading, {"class": _.compact(className), status: isLoading.value}, [data]);
        }
      }
    });

    const modalClassName = [
      "max-w-[initial]",
      "deep-[.el-message-box__container]:static",
      "deep-[.el-message-box__content]:p-0",
      "deep-[.el-message-box__message]:w-full",
      "absolute",
    ];
    if (safeGet(option, "type") === "drawer") {
      modalClassName.push("pb-0", "rounded-none", "top-0", "right-0", "h-full", "deep-[.el-message-box__btns]:hidden");
    } else {
      modalClassName.push("left-1/2", "transform", "-translate-x-1/2");
    }

    if (safeGet(option, "center")) {
      if (safeGet(option, "type") !== "drawer") {
        modalClassName.push("top-1/2", "-translate-y-1/2");
      }
    } else {
      modalClassName.push("block");
      if (safeGet(option, "type") !== "drawer") {
        if (_.hasIn(option, "top")) {
          modalClassName.push(safeGet(option, "top"));
        } else {
          modalClassName.push("top-[20%]");
        }
      }
    }


    modalConfig = {
      closeOnClickModal: false,
      closeOnPressEscape: false, // esc 键盘按钮
      showCancelButton: true,  // 取消按钮
      showConfirmButton: true, // 确定按钮
      ..._.omit(option, ["width", "height", "class", "type", "onOk", "onCancel", "center"]),
      title,
      center: false,
      message: createElement(Message),
      customClass: _.compact(_.concat(modalClassName, safeGet(option, "width") || "w-150")).join(" "),
      beforeClose (action, state, done) {
        return onActionHook(action, done);
      }
    }

    try {
      await ElMessageBox(modalConfig);
    } catch (error) {
      // todo
    }
    resolve(result);
  })
}

/**
 * 询问对话框
 * @param {*} content 提示内容
 * @param {*} title   标题
 * @param {*} type    图标类型
 * @returns
 */
export const sure = function (content = "", title, type = "info", option = {}) {
  const config = {
    height: [],
    width: "w-100",
    className: "p-5",
    confirmButtonText: i18n('common.confirm'),
    confirmButtonClass: 'el-button--primary',
    closeOnPressEscape: true,
    ...option
  };
  const view = [];
  if (type) {
    switch (_.trim(_.toLower(type))) {
      case "success":
        view.push((<ElIcon class="text-2xl text-[var(--el-color-success)]"><SuccessFilled/></ElIcon>));
        break;
      case "info":
        view.push((<ElIcon class="text-2xl text-[var(--el-color-info)]"><InfoFilled/></ElIcon>));
        break;
      case "warning":
        view.push((<ElIcon class="text-2xl text-[var(--el-color-warning)]"><WarningFilled/></ElIcon>));
        break;
      case "error":
        view.push((<ElIcon class="text-2xl text-[var(--el-color-error)]"><CircleCloseFilled/></ElIcon>));
        break;
    }
  }
  view.push(<div class="ml-2 first:ml-0 text-base">{content}</div>);

  const Message = defineComponent({
    setup (props, {expose}) {
      expose({submit: () => true});
      return () => {
        return <div class="flex items-center justify-start">{view}</div>;
      }
    }
  });
  return confirm(Message, title || i18n('common.prompt'), config);
}

export const success = function (content = "", title, option = {}) {
  return sure(content, title, "success", option);
}

export const warning = function (content = "", title, option = {}) {
  return sure(content, title, "warning", option);
}

export const info = function (content = "", title, option = {}) {
  return sure(content, title, "info", option);
}

export const error = function (content = "", title, option = {}) {
  return sure(content, title, "error", option);
}

export const alert = function (content = "", title) {
  const config = {
    height: ["min-h-30"],
    width: "w-120",
    className: "p-5",
    showCancelButton: false,  // 取消按钮
    confirmButtonText: i18n('common.submit'),
    confirmButtonClass: 'el-button--primary',
  };
  const Message = defineComponent({
    setup (props, {expose}) {
      expose({submit: () => true});
      return () => {
        return <div className="text-base">{content}</div>;
      }
    }
  });
  return confirm(Message, title || i18n('common.prompt'), config);
}

/**
 * 动态表单
 * @param {*} title  弹框标题
 * @param {*} items  表单元素
 * @param {*} option 弹框配置
 * @param {*} props  表单 el-form 组件 props 配置
 * @returns
 */
export const form = function (title, items = [], option = {}, props = {}) {
  return confirm(formUI.Form, title, option, {items, option: props || {}});
}


/**
 * 抽屉
 * @param {*} content  内容
 * @param {*} title    标题
 * @param {*} option   配置
 * @param {*} props    如果 content 为 VNode, 则会传递给 content
 * @returns
 */
export const drawer = function (content, option = {}, props) {
  return confirm(content, void 0, {
    ...option,
    type: "drawer",
    "class": ["p-0"]
  }, props);
}

export const prompt = async function (title, value) {
  const data = await form(title, [{
    value,
    key: "text",
    component: ElInput,
    // pleaseEnter
    props: {
      maxlength: 200,
      autofocus: false,
      clearable: true,
      showWordLimit: true,
      placeholder: i18n.join("common.pleaseEnter", title)
    },
    rules: formUI.rules.text()
  }], {
    width: "w-100",
    height: [],
    closeOnPressEscape: true,
  });
  return data?.text || "";
}